import { z } from 'zod';

import { Id } from '../common/primitives';
import { FraudSeveritySchema, PricingSignalKindSchema, PricingSignalSchema } from '../intelligence';

export { Paise } from '../common/primitives';

// ── Recommendation surfaces ────────────────────────────────────────────────

export const RecommendationSurfaceSchema = z.enum([
  'SIMILAR', // shoppers also viewed
  'CROSS_SELL', // frequently bought together
  'UPSELL', // higher tier of same category
  'PERSONALIZED', // home page, per-user
  'CART', // suggestions for an active cart
  'RECENTLY_VIEWED',
  'B2B_REORDER', // suggested SKUs to reorder for a business account
]);
export type RecommendationSurface = z.infer<typeof RecommendationSurfaceSchema>;

export const RecommendationItemSchema = z.object({
  productId: Id,
  /// Confidence score 0..10000. Higher = stronger match.
  scoreBps: z.number().int(),
  /// Why this product was picked — surface-specific reason code.
  reason: z.string(),
});
export type RecommendationItem = z.infer<typeof RecommendationItemSchema>;

export const RecommendationResultSchema = z.object({
  surface: RecommendationSurfaceSchema,
  /// The seed entity the recommendations are based on (productId, cartId, userId...). Optional.
  seedId: z.string().nullable(),
  items: z.array(RecommendationItemSchema),
});
export type RecommendationResult = z.infer<typeof RecommendationResultSchema>;

export const RecommendationQuery = z
  .object({
    limit: z.coerce.number().int().min(1).max(50).default(10),
  })
  .strict();
export type RecommendationQuery = z.infer<typeof RecommendationQuery>;

// ── Pricing engine ────────────────────────────────────────────────────────

export const PricingScanRequest = z
  .object({
    /// Limit candidate variants per detector.
    limit: z.number().int().min(1).max(5000).default(500),
    /// Detector overrides. Defaults to all rules-based detectors.
    detectors: z.array(z.enum(['STOCK_PRESSURE', 'DEMAND_SPIKE', 'CLEARANCE'])).optional(),
    /// Default validity window for proposed signals (hours). Default 48h.
    validityHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 30)
      .default(48),
  })
  .strict();
export type PricingScanRequest = z.infer<typeof PricingScanRequest>;

export const PricingScanResultSchema = z.object({
  scanned: z.number().int().nonnegative(),
  proposed: z.array(PricingSignalSchema),
  durationMs: z.number().int().nonnegative(),
});
export type PricingScanResult = z.infer<typeof PricingScanResultSchema>;

// ── Background Jobs ───────────────────────────────────────────────────────

export const AiJobStatusSchema = z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'ROLLED_BACK']);
export type AiJobStatus = z.infer<typeof AiJobStatusSchema>;

export const AiJobSchema = z.object({
  id: z.string(),
  kind: z.string(),
  status: AiJobStatusSchema,
  progress: z.number().int(),
  totalItems: z.number().int(),
  result: z.unknown().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AiJob = z.infer<typeof AiJobSchema>;

export const AiJobStartResultSchema = z.object({
  jobId: z.string(),
});
export type AiJobStartResult = z.infer<typeof AiJobStartResultSchema>;

// ── Fraud engine ──────────────────────────────────────────────────────────

export const FraudScanRequest = z
  .object({
    limit: z.number().int().min(1).max(5000).default(500),
    detectors: z
      .array(
        z.enum([
          'REFUND_VELOCITY',
          'WITHDRAWAL_ANOMALY',
          'NEW_VENDOR_BURST',
          'COUPON_STACK',
          'WALLET_DRAIN',
        ]),
      )
      .optional(),
  })
  .strict();
export type FraudScanRequest = z.infer<typeof FraudScanRequest>;

export const FraudScanFlagSchema = z.object({
  detectorKey: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  score: z.number().int().min(0).max(100),
  severity: FraudSeveritySchema,
  context: z.unknown().nullable(),
});
export type FraudScanFlag = z.infer<typeof FraudScanFlagSchema>;

export const FraudScanResultSchema = z.object({
  scanned: z.number().int().nonnegative(),
  flagged: z.number().int().nonnegative(),
  detectorsRun: z.array(z.string()),
  durationMs: z.number().int().nonnegative(),
});
export type FraudScanResult = z.infer<typeof FraudScanResultSchema>;

// ── Content engine ────────────────────────────────────────────────────────

export const ContentTagSchema = z.object({
  productId: Id,
  /// Free-form keywords / categories extracted from title + description.
  tags: z.array(z.string()),
  /// Heuristic SEO suggestions: title, meta description.
  seo: z.object({
    title: z.string(),
    description: z.string(),
  }),
});
export type ContentTag = z.infer<typeof ContentTagSchema>;

export const TagProductRequest = z
  .object({
    productId: Id,
    /// If true, the seo/tags suggestions are written back to the Product row's attributes JSON.
    persist: z.boolean().default(false),
  })
  .strict();
export type TagProductRequest = z.infer<typeof TagProductRequest>;

// Re-export downstream types so the FE only needs to depend on `ai`.
export { PricingSignalKindSchema };
